import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Leaf } from "lucide-react";

export default function AdminLogin() {
    const nav = useNavigate();
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) nav("/admin", { replace: true });
    }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back!");
            nav("/admin", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background grid lg:grid-cols-2" data-testid="admin-login-page">
            <div className="hidden lg:block relative overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/10326904/pexels-photo-10326904.jpeg?auto=compress&cs=tinysrgb&w=940"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-primary/85" />
                <div className="relative z-10 p-12 lg:p-16 h-full flex flex-col justify-between text-primary-foreground">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="h-9 w-9 rounded-sm bg-primary-foreground text-primary grid place-items-center">
                            <Leaf className="h-5 w-5" />
                        </span>
                        <span className="font-display text-lg">Tishya Foundation</span>
                    </Link>
                    <div>
                        <div className="font-eyebrow text-xs text-secondary mb-4">Admin</div>
                        <h1 className="font-display text-5xl leading-[1.05]">
                            Manage content,
                            <br />
                            <span className="italic font-light">measure impact.</span>
                        </h1>
                        <p className="mt-5 text-primary-foreground/80 max-w-md text-sm leading-relaxed">
                            Sign in to update programs, trustees, projects, gallery and respond to inquiries.
                        </p>
                    </div>
                </div>
            </div>

            <div className="grid place-items-center p-6 lg:p-12">
                <form onSubmit={submit} className="w-full max-w-md space-y-6">
                    <div>
                        <div className="font-eyebrow text-xs text-secondary mb-3">Welcome back</div>
                        <h2 className="font-display text-3xl lg:text-4xl">Sign in to the dashboard</h2>
                    </div>
                    <div>
                        <Label htmlFor="al-email" className="font-eyebrow text-xs">Email</Label>
                        <Input
                            id="al-email"
                            data-testid="admin-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label htmlFor="al-password" className="font-eyebrow text-xs">Password</Label>
                        <Input
                            id="al-password"
                            data-testid="admin-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1.5"
                        />
                    </div>
                    <Button
                        type="submit"
                        data-testid="admin-login-submit"
                        disabled={loading}
                        className="w-full rounded-full h-12 bg-primary hover:bg-primary/90"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                        Default: admin@tishyafoundation.org / Tishya@2026
                    </p>
                </form>
            </div>
        </div>
    );
}
